import React, {useCallback, useState} from "react";
import {Button} from "@aws-amplify/ui-react";
import {BookmarkAdd, BookmarkRemove} from "@mui/icons-material";
import {CircularProgress} from "@mui/material";
import {toast} from "react-toastify";
import {addPendingOrder, removePendingOrder} from "../infrastructure/pendingOrderApi";
import {isSuccess} from "../../common/domain/RemoteData.model";
import {useComercialContext} from "../../comercial/ComercialContext";

/**
 * Botón de pendientes de la ficha y de la tarjeta de producto.
 *
 * Es un toggle sobre el pendiente vivo del producto:
 * - Si el cliente NO tiene pendiente, añade uno con la cantidad seleccionada
 *   en ese contexto (POST account/products/pending?code=X&quantity=N).
 * - Si ya lo tiene, lo quita entero (DELETE account/products/pending?code=X).
 *
 * @param {{
 *     productCode: string,
 *     defaultValue: boolean,
 *     quantity?: number,
 *     showText?: boolean,
 * }} props
 */
export const PendingProductButton = ({productCode, defaultValue, quantity = 1, showText = false}) => {
    const [isPending, setIsPending] = useState(defaultValue);
    const [loading, setLoading] = useState(false);
    const {actingAsCliente} = useComercialContext();

    const handleClick = useCallback(async () => {
        setLoading(true);
        try {
            const actingAs = actingAsCliente?.numCliente ?? null;
            if (isPending) {
                const result = await removePendingOrder(productCode, actingAs);
                if (isSuccess(result)) {
                    setIsPending(false);
                    toast.success('Producto quitado de pendientes');
                } else {
                    toast.error('Error al quitar el producto de pendientes');
                }
            } else {
                const units = Math.max(quantity, 1);
                const result = await addPendingOrder(productCode, units, actingAs);
                if (isSuccess(result)) {
                    setIsPending(true);
                    toast.success(
                        units > 1
                            ? `Producto añadido a pendientes (${units} ud.)`
                            : 'Producto añadido a pendientes'
                    );
                } else {
                    toast.error('Error al añadir el producto a pendientes');
                }
            }
        } catch (error) {
            console.error('Failed to add/remove pending order:', error);
            toast.error(
                isPending
                    ? 'Error al quitar el producto de pendientes'
                    : 'Error al añadir el producto a pendientes'
            );
        } finally {
            setLoading(false);
        }
    }, [isPending, productCode, quantity, actingAsCliente?.numCliente]);

    const getButtonIcon = () => {
        if (loading) {
            return <CircularProgress size={20}/>;
        }
        return isPending ? <BookmarkRemove/> : <BookmarkAdd/>;
    };

    const label = isPending ? 'Quitar de pendientes' : 'Añadir a pendientes';

    return (
        <div className="d-flex gap-1 align-items-center">
            <Button
                variant="contained"
                className="btn btn-warning"
                onClick={handleClick}
                disabled={loading}
                title={label}
            >
                {getButtonIcon()}
            </Button>
            {showText && <span className="text-warning">{label}</span>}
        </div>
    );
};
